import React, { useState, useContext, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonTextarea,
  IonFooter,
  useIonToast,
} from '@ionic/react';
import { banOutline } from 'ionicons/icons';
import { useHistory, useParams } from 'react-router-dom';
import { isDesktop } from '../../utils/platformUtils';
import { WebLayout } from '../../components/layouts/WebLayout';
import {
  doc,
  increment,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { ProfileContext } from '../../context/ProfileContext';
import { LoadingScreen } from '../../components/LoadingScreen';
import { getPaymentReceiptById, updateReceiptNotes } from '../../functions/Fx_Payment';
import { PaymentReceipt } from '../../types/payment';
import {
  formatCurrency,
  formatISODateTime,
  formatDate,
} from '../../utils/paymentCalculations';

const PaymentReceiptPage: React.FC = () => {
  const history = useHistory();
  // schedulerUid is only present on the werker view route
  const { receiptId, schedulerUid: routeSchedulerUid } = useParams<{ receiptId: string; schedulerUid?: string }>();
  const { profile } = useContext(ProfileContext);
  const [presentToast] = useIonToast();

  // When schedulerUid is in the route we are in read-only Werker view
  const isWerkerView = !!routeSchedulerUid;
  const effectiveSchedulerUid = routeSchedulerUid || profile.UserId;

  const [receipt, setReceipt] = useState<PaymentReceipt | null>(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadReceipt = async () => {
      if (!effectiveSchedulerUid || !receiptId) return;

      try {
        setIsLoading(true);
        const receiptData = await getPaymentReceiptById(effectiveSchedulerUid, receiptId);
        if (receiptData) {
          setReceipt(receiptData);
          setNotes(receiptData.notes || '');

          // Mark as read for the Werker when they open the detail page
          if (isWerkerView && !receiptData.werkerIsRead && profile.UserId) {
            try {
              await updateDoc(
                doc(db, `users/${effectiveSchedulerUid}/paymentReceipts/${receiptId}`),
                { werkerIsRead: true, werkerReadAt: serverTimestamp() }
              );
              await setDoc(
                doc(db, `users/${profile.UserId}/users_PaymentReceiptsCount`, profile.UserId),
                { paymentReceiptsUnreadCount: increment(-1), uid: profile.UserId },
                { merge: true }
              );
            } catch (e) {
              // no-op — badge update is best-effort
            }
          }
        } else {
          presentToast({ message: 'Receipt not found', duration: 3000, position: 'top', color: 'danger' });
          history.replace(isWerkerView ? '/Notifications' : '/payments');
        }
      } catch (error) {
        console.error('Error loading receipt:', error);
        presentToast({ message: 'Error loading receipt', duration: 3000, position: 'top', color: 'danger' });
        history.replace(isWerkerView ? '/Notifications' : '/payments');
      } finally {
        setIsLoading(false);
      }
    };

    loadReceipt();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSchedulerUid, receiptId]);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setHasChanges(value !== (receipt?.notes || ''));
  };

  const handleSaveNotes = async () => {
    if (!receipt || !profile.UserId) return;

    try {
      setIsSaving(true);
      await updateReceiptNotes(profile.UserId, receipt.receiptId, notes);

      // Update local state
      setReceipt({ ...receipt, notes, updatedAt: Date.now() });
      setHasChanges(false);

      presentToast({
        message: 'Notes saved',
        duration: 2000,
        position: 'top',
        color: 'success',
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      presentToast({
        message: 'Error saving notes',
        duration: 3000,
        position: 'top',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Desktop layout ──────────────────────────────────────────────────────────
  // Check desktop first so the mobile branch can keep IonPage mounted during loading
  // (prevents page-overlap during the iOS slide transition).
  if (isDesktop()) {
    if (isLoading || !receipt) {
      return <LoadingScreen LoadingText="Loading receipt..." />;
    }
    return (
      <WebLayout title="Payment Receipt">
        <div style={{ height: '100%', overflowY: 'auto', padding: '24px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>

            {/* Header info */}
            <div style={{ marginBottom: '24px' }}>
              <IonText color="medium"><p style={{ margin: '4px 0', fontSize: '14px' }}>PAID FROM</p></IonText>
              <IonText><h2 style={{ margin: '4px 0' }}>{receipt.schedulerName}</h2></IonText>
              <IonText color="medium" style={{ marginTop: '16px', display: 'block' }}>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>PAID TO</p>
              </IonText>
              <IonText><h2 style={{ margin: '4px 0' }}>{receipt.werkerName}</h2></IonText>
              <IonText color="medium" style={{ marginTop: '16px', display: 'block' }}>
                <p style={{ margin: '4px 0', fontSize: '14px' }}>PAID DATE</p>
              </IonText>
              <IonText><h2 style={{ margin: '4px 0' }}>{formatDate(receipt.paymentDate)}</h2></IonText>
            </div>

            {/* Line items */}
            <IonText color="medium"><h3 style={{ marginTop: '24px', marginBottom: '8px' }}>LINE ITEMS</h3></IonText>
            <IonList>
              {receipt.jobs.map((job) => (
                <IonItem key={job.jobDocId} lines="full">
                  <IonLabel>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {job.company}
                      {job.isCancelled && (
                        <IonText color="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'normal' }}>
                          <IonIcon icon={banOutline} style={{ fontSize: '14px' }} />
                          Cancelled
                        </IonText>
                      )}
                    </h3>
                    <p style={{ margin: '2px 0' }}>Start: {formatISODateTime(job.dateTimeStart)}</p>
                    <p style={{ margin: '2px 0' }}>Finish: {formatISODateTime(job.dateTimeFinish)}</p>
                    <p>Pay: {job.pay}</p>
                  </IonLabel>
                  <IonText slot="end" color="dark">
                    <strong>{formatCurrency(job.paidAmount)}</strong>
                  </IonText>
                </IonItem>
              ))}
            </IonList>

            {/* Total */}
            <div style={{ marginTop: '16px', padding: '16px', background: 'var(--ion-color-light)', borderRadius: '8px' }}>
              <IonText color="medium"><p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>TOTAL PAID</p></IonText>
              <IonText color="success"><h1 style={{ margin: 0 }}>{formatCurrency(receipt.totalAmount)}</h1></IonText>
            </div>

            {/* Notes */}
            <div style={{ marginTop: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <IonText color="medium"><h3 style={{ margin: 0 }}>NOTES</h3></IonText>
                {!isWerkerView && hasChanges && (
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSaving}
                    style={{
                      backgroundColor: '#ff7900', color: '#000', border: 'none',
                      borderRadius: '6px', padding: '6px 14px', fontSize: '14px',
                      fontWeight: 'bold', cursor: isSaving ? 'default' : 'pointer',
                      opacity: isSaving ? 0.6 : 1,
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>
              {isWerkerView ? (
                <IonText color="medium">
                  <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{notes || 'No notes.'}</p>
                </IonText>
              ) : (
                <IonTextarea
                  value={notes}
                  onIonChange={(e) => handleNotesChange(e.detail.value || '')}
                  placeholder="Add notes about this payment..."
                  autoGrow
                  rows={4}
                  style={{ border: '1px solid var(--ion-color-medium)', borderRadius: '8px', padding: '8px' }}
                />
              )}
            </div>

            {/* Metadata */}
            <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid var(--ion-color-light)' }}>
              <IonText color="medium">
                <p style={{ fontSize: '12px', margin: '4px 0' }}>Receipt ID: {receipt.receiptId}</p>
                <p style={{ fontSize: '12px', margin: '4px 0' }}>Created: {formatDate(receipt.createdAt)}</p>
                {receipt.updatedAt && receipt.updatedAt !== receipt.createdAt && (
                  <p style={{ fontSize: '12px', margin: '4px 0' }}>Updated: {formatDate(receipt.updatedAt)}</p>
                )}
              </IonText>
            </div>

            {/* Done button */}
            <div style={{ marginTop: '24px', textAlign: 'right' }}>
              <button
                onClick={() => {
                  if (isWerkerView) {
                    history.replace('/Notifications');
                  } else {
                    sessionStorage.setItem('paymentsRefresh', 'true');
                    sessionStorage.setItem('paymentsSegment', 'history');
                    history.goBack();
                  }
                }}
                style={{
                  backgroundColor: '#ff7900',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 28px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>

          </div>
        </div>
      </WebLayout>
    );
  }

  // ─── Mobile layout ───────────────────────────────────────────────────────────
  // Keep IonPage mounted unconditionally so Ionic owns the route transition.
  // The `bckgrnd` class makes IonPage opaque during loading so the previous page
  // doesn't bleed through the transparent LoadingScreen during the slide.
  const mobileLoading = isLoading || !receipt;
  return (
    <IonPage className={mobileLoading ? "bckgrnd" : ""}>
      {mobileLoading ? (
        <LoadingScreen LoadingText="Loading receipt..." />
      ) : (
        <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Payment Receipt</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollX={false} scrollY={true} className="ion-padding">
        {/* Header Information */}
        <div style={{ marginBottom: '24px' }}>
          <IonText color="medium">
            <p style={{ margin: '4px 0', fontSize: '14px' }}>PAID FROM</p>
          </IonText>
          <IonText>
            <h2 style={{ margin: '4px 0' }}>{receipt.schedulerName}</h2>
          </IonText>

          <IonText color="medium" style={{ marginTop: '16px', display: 'block' }}>
            <p style={{ margin: '4px 0', fontSize: '14px' }}>PAID TO</p>
          </IonText>
          <IonText>
            <h2 style={{ margin: '4px 0' }}>{receipt.werkerName}</h2>
          </IonText>

          <IonText color="medium" style={{ marginTop: '16px', display: 'block' }}>
            <p style={{ margin: '4px 0', fontSize: '14px' }}>PAID DATE</p>
          </IonText>
          <IonText>
            <h2 style={{ margin: '4px 0' }}>{formatDate(receipt.paymentDate)}</h2>
          </IonText>
        </div>

        {/* Line Items */}
        <IonText color="medium">
          <h3 style={{ marginTop: '24px', marginBottom: '8px' }}>LINE ITEMS</h3>
        </IonText>

        <IonList>
          {receipt.jobs.map((job) => (
            <IonItem key={job.jobDocId} lines="full">
              <IonLabel>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {job.company}
                  {job.isCancelled && (
                    <IonText color="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'normal' }}>
                      <IonIcon icon={banOutline} style={{ fontSize: '14px' }} />
                      Cancelled
                    </IonText>
                  )}
                </h3>
                <p style={{ margin: '2px 0' }}>Start: {formatISODateTime(job.dateTimeStart)}</p>
                <p style={{ margin: '2px 0' }}>Finish: {formatISODateTime(job.dateTimeFinish)}</p>
                <p>Pay: {job.pay}</p>
              </IonLabel>
              <IonText slot="end" color="dark">
                <strong>{formatCurrency(job.paidAmount)}</strong>
              </IonText>
            </IonItem>
          ))}
        </IonList>

        {/* Total */}
        <div
          style={{
            marginTop: '16px',
            padding: '16px',
            background: 'var(--ion-color-light)',
            borderRadius: '8px',
          }}
        >
          <IonText color="medium">
            <p style={{ margin: '0 0 4px 0', fontSize: '14px' }}>TOTAL PAID</p>
          </IonText>
          <IonText color="success">
            <h1 style={{ margin: 0 }}>{formatCurrency(receipt.totalAmount)}</h1>
          </IonText>
        </div>

        {/* Notes Section */}
        <div style={{ marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <IonText color="medium">
              <h3 style={{ margin: 0 }}>NOTES</h3>
            </IonText>
            {!isWerkerView && hasChanges && (
              <button
                onClick={handleSaveNotes}
                disabled={isSaving}
                style={{
                  backgroundColor: '#ff7900',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: isSaving ? 'default' : 'pointer',
                  opacity: isSaving ? 0.6 : 1,
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
          {isWerkerView ? (
            // Werker sees notes as read-only text
            <IonText color="medium">
              <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {notes || 'No notes.'}
              </p>
            </IonText>
          ) : (
            <IonTextarea
              value={notes}
              onIonChange={(e) => handleNotesChange(e.detail.value || '')}
              placeholder="Add notes about this payment..."
              autoGrow
              rows={4}
              style={{
                border: '1px solid var(--ion-color-medium)',
                borderRadius: '8px',
                padding: '8px',
              }}
            />
          )}
        </div>

        {/* Receipt Metadata */}
        <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid var(--ion-color-light)' }}>
          <IonText color="medium">
            <p style={{ fontSize: '12px', margin: '4px 0' }}>Receipt ID: {receipt.receiptId}</p>
            <p style={{ fontSize: '12px', margin: '4px 0' }}>
              Created: {formatDate(receipt.createdAt)}
            </p>
            {receipt.updatedAt && receipt.updatedAt !== receipt.createdAt && (
              <p style={{ fontSize: '12px', margin: '4px 0' }}>
                Updated: {formatDate(receipt.updatedAt)}
              </p>
            )}
          </IonText>
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => {
              if (isWerkerView) {
                history.replace('/Notifications');
              } else {
                sessionStorage.setItem('paymentsRefresh', 'true');
                sessionStorage.setItem('paymentsSegment', 'history');
                history.goBack();
              }
            }}
          >
            Done
          </IonButton>
        </IonToolbar>
      </IonFooter>
        </>
      )}
    </IonPage>
  );
};

export default PaymentReceiptPage;
